import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  Loader2, ArrowLeft, Play, TrendingUp, TrendingDown, DollarSign,
  Target, AlertTriangle, Clock, BarChart3, PieChart, RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const BacktestDashboard = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const { toast } = useToast();
  const { isAdmin } = useAuth();

  const { data: status, isLoading, refetch } = useQuery({
    queryKey: ['backtest-status'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('run-backtest-simulation', {
        body: { action: 'status' },
      });
      if (error) throw error;
      return data;
    },
    refetchInterval: 60000,
  });

  const runDailyTrade = async () => {
    setIsRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke('run-backtest-simulation', {
        body: { action: 'daily-run' },
      });
      if (error) throw error;
      toast({ title: '✅ Daily Trade', description: data.message });
      refetch();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setIsRunning(false);
    }
  };

  const checkExits = async () => {
    setIsChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke('run-backtest-simulation', {
        body: { action: 'check-exits' },
      });
      if (error) throw error;
      toast({ title: '🔍 Exit Check', description: data.message });
      refetch();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setIsChecking(false);
    }
  };

  const fmt = (n: number | null | undefined, decimals = 2) => {
    if (n == null) return 'A$0.00';
    return `A$${n.toFixed(decimals)}`;
  };

  const pctFmt = (n: number | null | undefined) => {
    if (n == null) return '0.00%';
    return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
  };

  const summary = status?.executive_summary;
  const openPositions = status?.open_positions || [];
  const closedTrades = status?.closed_trades || [];
  const weeklyBreakdown = status?.weekly_breakdown || [];

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-4 py-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2 mb-4">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-3xl font-bold mb-1 flex items-center gap-3">
            <BarChart3 className="w-7 h-7" />
            GoToCoin Backtest Simulator
          </h1>
          <p className="text-muted-foreground text-sm">
            $50/day conservative swing trading • +25% TP • -12% SL • 14-day max hold
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl space-y-6">
        {/* Controls */}
        {isAdmin && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Simulation Controls</CardTitle>
            </CardHeader>
            <CardContent className="flex gap-3 flex-wrap">
              <Button onClick={runDailyTrade} disabled={isRunning} size="sm">
                {isRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Execute Today's Trade
              </Button>
              <Button onClick={checkExits} disabled={isChecking} variant="outline" size="sm">
                {isChecking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Check Exit Triggers
              </Button>
              <Button onClick={() => refetch()} variant="ghost" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </CardContent>
          </Card>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : !summary ? (
          <Card>
            <CardContent className="py-12 text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No trades yet</h3>
              <p className="text-muted-foreground mb-4">
                The simulation starts fresh from today. Click "Execute Today's Trade" to begin.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Executive Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard icon={<DollarSign className="w-5 h-5" />} label="Total Invested" value={fmt(summary.total_invested)} />
              <SummaryCard
                icon={<TrendingUp className="w-5 h-5" />}
                label="Portfolio Value"
                value={fmt(summary.portfolio_value)}
                change={pctFmt(summary.total_return_pct)}
                positive={summary.total_return >= 0}
              />
              <SummaryCard icon={<Target className="w-5 h-5" />} label="Win Rate" value={`${summary.win_rate.toFixed(1)}%`} />
              <SummaryCard icon={<PieChart className="w-5 h-5" />} label="Total Trades" value={String(summary.total_trades)} />
            </div>

            {/* Performance Metrics */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">📊 Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <MetricItem label="Open Positions" value={String(summary.open_positions)} />
                  <MetricItem label="Closed Positions" value={String(summary.closed_positions)} />
                  <MetricItem label="Avg Win" value={pctFmt(summary.avg_win)} positive />
                  <MetricItem label="Avg Loss" value={pctFmt(summary.avg_loss)} />
                  <MetricItem label="Best Trade" value={summary.best_trade ? `${pctFmt(summary.best_trade.pnl_pct)} (${summary.best_trade.symbol})` : 'N/A'} positive />
                  <MetricItem label="Worst Trade" value={summary.worst_trade ? `${pctFmt(summary.worst_trade.pnl_pct)} (${summary.worst_trade.symbol})` : 'N/A'} />
                  <MetricItem label="Avg Hold Time" value={`${summary.avg_hold_time.toFixed(1)} days`} />
                  <MetricItem label="Realized P/L" value={fmt(summary.realized_pnl)} positive={summary.realized_pnl >= 0} />
                </div>
              </CardContent>
            </Card>

            {/* Target Hit Analysis */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">🎯 Exit Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <div className="text-2xl font-bold text-green-600">{summary.target_hits}</div>
                    <div className="text-xs text-muted-foreground">Target Hits (+25%)</div>
                  </div>
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                    <div className="text-2xl font-bold text-red-600">{summary.stop_hits}</div>
                    <div className="text-xs text-muted-foreground">Stop Losses (-12%)</div>
                  </div>
                  <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                    <div className="text-2xl font-bold text-yellow-600">{summary.timeouts}</div>
                    <div className="text-xs text-muted-foreground">Timeouts (14 days)</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Open Positions */}
            {openPositions.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">📈 Open Positions ({openPositions.length}/10)</CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Coin</TableHead>
                        <TableHead>Entry Date</TableHead>
                        <TableHead className="text-right">Entry A$</TableHead>
                        <TableHead className="text-right">Current A$</TableHead>
                        <TableHead className="text-right">P/L %</TableHead>
                        <TableHead className="text-right">Days</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {openPositions.map((pos: any, i: number) => (
                        <TableRow key={i}>
                          <TableCell className="font-semibold">{pos.symbol}</TableCell>
                          <TableCell className="text-muted-foreground">{pos.entry_date}</TableCell>
                          <TableCell className="text-right">{fmt(pos.entry_price, 4)}</TableCell>
                          <TableCell className="text-right">{fmt(pos.current_price, 4)}</TableCell>
                          <TableCell className={`text-right font-medium ${(pos.pnl_pct || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {pctFmt(pos.pnl_pct)}
                          </TableCell>
                          <TableCell className="text-right">{pos.days_held || 0}</TableCell>
                          <TableCell>
                            <Badge variant={(pos.pnl_pct || 0) >= 0 ? 'default' : 'destructive'} className="text-xs">
                              {(pos.pnl_pct || 0) >= 0 ? 'Winning' : 'Losing'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Closed Trades */}
            {closedTrades.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">📋 Closed Trades</CardTitle>
                  <CardDescription>Most recent first</CardDescription>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Coin</TableHead>
                        <TableHead>Entry</TableHead>
                        <TableHead className="text-right">Entry A$</TableHead>
                        <TableHead>Exit</TableHead>
                        <TableHead className="text-right">Exit A$</TableHead>
                        <TableHead className="text-right">Days</TableHead>
                        <TableHead className="text-right">P/L %</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead className="text-right">Net P/L</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {closedTrades.map((t: any, i: number) => (
                        <TableRow key={i}>
                          <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                          <TableCell className="font-semibold">{t.symbol}</TableCell>
                          <TableCell className="text-muted-foreground text-xs">{t.entry_date}</TableCell>
                          <TableCell className="text-right">{fmt(t.entry_price, 4)}</TableCell>
                          <TableCell className="text-muted-foreground text-xs">{t.exit_date}</TableCell>
                          <TableCell className="text-right">{fmt(t.exit_price, 4)}</TableCell>
                          <TableCell className="text-right">{t.days_held}</TableCell>
                          <TableCell className={`text-right font-medium ${(t.pnl_pct || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {pctFmt(t.pnl_pct)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs whitespace-nowrap">
                              {t.exit_reason}
                            </Badge>
                          </TableCell>
                          <TableCell className={`text-right font-medium ${(t.net_pnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {fmt(t.net_pnl)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Weekly Breakdown */}
            {weeklyBreakdown.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">📅 Weekly Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {weeklyBreakdown.map((week: any, i: number) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                          <span className="font-medium text-sm">Week of {week.weekStart}</span>
                          <div className="text-xs text-muted-foreground">
                            Trades: {week.trades} | W: {week.wins} | L: {week.losses} | Open: {week.open}
                          </div>
                        </div>
                        <span className={`font-semibold ${week.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {fmt(week.pnl)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Trading Rules */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">📝 Trading Rules</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>• <strong>Entry:</strong> A$50/day into top TOSS-rated Swyftx token at market price (0.6% fee = A$50.30 cost)</p>
                <p>• <strong>Take Profit:</strong> +25% from entry → auto-sell entire position</p>
                <p>• <strong>Stop Loss:</strong> -12% from entry → auto-sell entire position</p>
                <p>• <strong>Time Limit:</strong> 14 days → force sell if neither target hit</p>
                <p>• <strong>Max Positions:</strong> 10 concurrent open trades (skip day if full)</p>
                <p>• <strong>Exit Fee:</strong> 0.6% deducted from sale proceeds</p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

function SummaryCard({ icon, label, value, change, positive }: {
  icon: React.ReactNode; label: string; value: string; change?: string; positive?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 text-muted-foreground mb-1">
          {icon}
          <span className="text-xs">{label}</span>
        </div>
        <div className="text-xl font-bold">{value}</div>
        {change && (
          <div className={`text-xs font-medium ${positive ? 'text-green-600' : 'text-red-600'}`}>
            {change}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetricItem({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-semibold ${positive === true ? 'text-green-600' : positive === false ? 'text-red-600' : ''}`}>
        {value}
      </div>
    </div>
  );
}

export default BacktestDashboard;
